import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
} from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { GoalStatus } from '../../../coaching-base/general';
import { TranslationKeyMap } from '../../coaching-insight.i18n';
import { prependEmptyOption } from '../models/coaching.global.model';
import { GoalHistory } from '../models/oa.history.model';
import { GoalMetric, goalVariancePercent, Metric } from '../models/oa.model';
import { SortDirection, SortingField } from '../models/sorting.model';
import { CoachingInsightWidgetService } from '../services/coaching-insight.widget.service';
import { CoachingService } from '../services/coaching.service';

import { AbstractOAHistory } from './oa-history-page.component';

@Component({
    selector: 'cmps-goals-history',
    templateUrl: './goals-history.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class GoalsHistoryComponent extends AbstractOAHistory<GoalHistory> {
    goalmetrics: GoalMetric[];

    allRecordsHidden = false;
    hasPendingData = false;
    failLoad = '';

    displayCommitments = false;
    defaultSortIndex = 0;

    i18n = TranslationKeyMap;

    constructor(
        protected _coachInsightService: CoachingInsightWidgetService,
        protected _coachingService: CoachingService,
        protected cd: ChangeDetectorRef,
        protected _translateService: TranslateService,
    ) {
        super(_coachInsightService, _coachingService, cd, _translateService);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    initData() {
        this.hasPendingData = true;

        this.sortFields = [
            new SortingField('metricName', 'string'),
            new SortingField('goalValue', 'number'),
            new SortingField('pctGoal', 'number'),
            new SortingField('improvement', 'number'),
            new SortingField('startActual', 'number'),
            new SortingField('startTarget', 'number'),
            new SortingField('completionDate', 'date'),
            new SortingField('coachName', 'string'),
            new SortingField('createdOn', 'date', SortDirection.DESC),
            new SortingField('createdBy', 'string'),
            new SortingField('statusName', 'string'),
            new SortingField('disposition', 'number'),
            new SortingField('commitmentCount', 'number'),
        ];

        const x = this._coachingService.retrieveGoalHistory(
            this.performerId,
            this._coachInsightService.getPeriodTypeId(),
            this.numRecords,
        );
        this.sub = x.subscribe(
            (s) => {
                if (s) {
                    this.processGoalsHistory(s);
                } else {
                    this.failLoad = this._translateService.instant(
                        TranslationKeyMap._7447_FAILED_TO_LOAD_DATA,
                    );
                }

                this.emitDisplayStatus();
                this.hasPendingData = false;
                this.cd.detectChanges();
            },
            () => {
                this.failLoad = this._translateService.instant(
                    TranslationKeyMap._7447_FAILED_TO_LOAD_DATA,
                );
                this.hasPendingData = false;
                this.cd.detectChanges();
            },
        );
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    showSubs() {
        this.displayCommitments = true;

        this.data.forEach((g: GoalHistory) => {
            g.rowSpan = g.goalmetrics ? g.goalmetrics.length : 1;
        });
        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    hideSubs() {
        this.displayCommitments = false;
        this.data.forEach((g: GoalHistory) => {
            g.rowSpan = 1;
        });
        this.cd.detectChanges();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    processGoalsHistory(s: any) {
        this.data = s.history as GoalHistory[];
        this.goalmetrics = s.goalMetrics;

        if (this.data) {
            this.data.forEach((g: GoalHistory) => {
                g.rowSpan = 1;
                g.display = true;

                const goalmetrics = this.goalmetrics.filter(
                    (m) => m.performanceGoalId === g.performanceGoalId,
                );
                g.goalmetrics = goalmetrics.length ? goalmetrics : undefined;
                g.pctGoal = goalVariancePercent(
                    g.startActual,
                    g.goalValue,
                    g.rankOrder === 0 ? -1 : 1,
                );

                // don't show disposition if NOT pending/pending close
                if (
                    g.statusId !== GoalStatus.PRE_APPROVAL &&
                    g.statusId !== GoalStatus.UPDATING &&
                    g.statusId !== GoalStatus.PENDING_CLOSE
                ) {
                    g.disposition = null;
                }
            });
        }

        this.processChildren(s);

        this.sortByFieldDefault();
        this.getDisplayable();
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    processChildren(s: any) {
        let metrics: Metric[] = [];
        let coaches: any[] = [];
        let statuses: any[] = [];
        let dispositions: any[] = [];
        let creators: any[] = [];

        if (this.filterOptions) {
            metrics = this.processMetrics(s.metrics);
            coaches = this.processCoaches(s.coaches);
            statuses = this.processStatuses(s.statuses);
            dispositions = this.processDispositions(s.dispositions);
            creators = this.processCreators(s.creators);

            this.filterOptions.Metrics = metrics;
            this.filterOptions.Creators = creators;
            this.filterOptions.Coaches = coaches;
            this.filterOptions.Statuses = statuses;
            this.filterOptions.Dispositions = dispositions;

            prependEmptyOption(
                this.filterOptions.Metrics,
                this.placeholderText,
            );
            prependEmptyOption(
                this.filterOptions.Creators,
                this.placeholderText,
            );
            prependEmptyOption(
                this.filterOptions.Coaches,
                this.placeholderText,
            );
            prependEmptyOption(
                this.filterOptions.Statuses,
                this.placeholderText,
            );
            prependEmptyOption(
                this.filterOptions.Dispositions,
                this.placeholderText,
            );
        }
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    processMetrics(metrics: any) {
        return metrics.map((d: Metric) => {
            if (d.metricId != null) {
                d.value = d.metricId.toString();
            }
            d.label =
                d.metricName ||
                this._translateService.instant(TranslationKeyMap._842_N_A);
            return d;
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    processCoaches(coaches: any) {
        return coaches.map((d: any) => {
            if (d.coachId != null) {
                d.value = d.coachId.toString();
            }
            d.label =
                d.coachName ||
                this._translateService.instant(TranslationKeyMap._842_N_A);
            return d;
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    processStatuses(statuses: any) {
        return statuses.map((d: any) => {
            if (d.statusId != null) {
                d.value = d.statusId.toString();
            }
            d.label =
                d.statusName ||
                this._translateService.instant(TranslationKeyMap._842_N_A);
            return d;
        });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    processDispositions(dispositions: any) {
        return dispositions
            .filter((d: any) => d.dispositionId && d.disposition) // remove dispositions with null values
            .map((d: any) => {
                d.value = d.dispositionId.toString();
                d.label = d.disposition;
                return d;
            });
    }

    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    processCreators(creators: any) {
        return creators.map((d: any) => {
            if (d.creatorId != null) {
                d.value = d.creatorId.toString();
            }
            d.label =
                d.creatorName ||
                this._translateService.instant(TranslationKeyMap._842_N_A);
            return d;
        });
    }
}
